import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { getPatientAccessDetailApi, listIncomingInvitesApi, listMyPatientsApi } from "@/portals/caregiver/api/caregiverApi";
import { getSocket } from "@/lib/socketClient";
import { useLiveRefreshFallback } from "@/shared/hooks/useLiveRefreshFallback";
import { toastError } from "@/shared/lib/toast";
import type { AppNotification } from "@/stores/notificationsStore";
import type { CaregiverPatientAccessDetail, CaregiverPatientSummary, IncomingCaregiverInvite } from "@/shared/types/caregiverPortal";

// Every notification type that means "something about a patient/relationship
// this caregiver can see just changed on the patient's side" — receiving any
// of these live-refreshes this context instead of waiting for a manual page
// reload (see the socket subscription below).
const RELEVANT_NOTIFICATION_TYPES: ReadonlySet<AppNotification["type"]> = new Set([
  "CAREGIVER_ACCESS_REVOKED",
  "CAREGIVER_ACCESS_UPDATED",
  "ACCESS_CATEGORY_APPROVED",
  "ACCESS_CATEGORY_DECLINED",
  "CAREGIVER_INVITE_ACCEPTED",
  "CAREGIVER_INVITE_CANCELLED",
  "ACCESS_REQUEST_APPROVED",
  "ACCESS_REQUEST_DECLINED",
]);

interface CaregiverPatientsContextValue {
  patients: CaregiverPatientSummary[];
  invites: IncomingCaregiverInvite[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** The patient whose shared records are currently displayed on the dashboard. */
  activePatientId: string | null;
  setActivePatientId: (patientId: string | null) => void;
  /** Cached per patient (keyed by patient id) so navigating to a category
   * page and back to the dashboard doesn't refetch/reload every time —
   * only fetched once per patient, then reused until explicitly refetched. */
  activePatientDetail: CaregiverPatientAccessDetail | null;
  isDetailLoading: boolean;
  /** Call after an action that actually changes what's shared (e.g.
   * requesting more access) to invalidate the cache for the active patient. */
  refetchActivePatientDetail: () => void;
}

const CaregiverPatientsContext = createContext<CaregiverPatientsContextValue | null>(null);

export function CaregiverPatientsProvider({ children }: { children: ReactNode }) {
  const [patients, setPatients] = useState<CaregiverPatientSummary[]>([]);
  const [invites, setInvites] = useState<IncomingCaregiverInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [activePatientId, setActivePatientId] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, CaregiverPatientAccessDetail>>({});
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  const fetchDetail = useCallback((patientId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) setIsDetailLoading(true);
    getPatientAccessDetailApi(patientId)
      .then(({ data, error: detailError }) => {
        if (detailError) {
          if (!options?.silent) toastError(detailError);
          return;
        }
        if (data) setDetailCache((prev) => ({ ...prev, [patientId]: data }));
      })
      .finally(() => {
        if (!options?.silent) setIsDetailLoading(false);
      });
  }, []);

  // Live refresh — no page reload needed when the patient revokes/restores/
  // changes access, or resolves an invite/request, while this dashboard is
  // already open. Silent on purpose: the patients list refetches in place
  // (see the isLoading fix below), and only the currently-viewed patient's
  // detail is silently re-fetched, keeping its old data on screen until the
  // fresh copy arrives — never a loading flash for something the user
  // didn't ask for. Other cached (not currently viewed) patients are just
  // dropped from the cache instead of force-refreshed — nothing is showing
  // them right now, so there's no flash risk, and selecting one later does
  // a normal (visibly loading) fetch instead of risking stale data.
  const refreshLive = useCallback(() => {
    refresh();
    setDetailCache((prev) => (activePatientId && prev[activePatientId] ? { [activePatientId]: prev[activePatientId] } : {}));
    if (activePatientId) fetchDetail(activePatientId, { silent: true });
  }, [activePatientId, refresh, fetchDetail]);

  useEffect(() => {
    const socket = getSocket();
    const handleNotification = (incoming: AppNotification) => {
      if (RELEVANT_NOTIFICATION_TYPES.has(incoming.type)) refreshLive();
    };
    socket.on("notification:new", handleNotification);
    return () => {
      socket.off("notification:new", handleNotification);
    };
  }, [refreshLive]);

  // Safety net for when the socket above isn't actually connected for any
  // reason — see useLiveRefreshFallback's own docstring. Never the primary
  // mechanism, just a guarantee this can't silently go stale forever.
  useLiveRefreshFallback(refreshLive);

  // Only fetches the first time a patient is selected — once cached, picking
  // a different patient and coming back (or navigating to a category page
  // and back) reuses the cached copy instead of reloading.
  useEffect(() => {
    if (!activePatientId || detailCache[activePatientId]) return;
    fetchDetail(activePatientId);
  }, [activePatientId, detailCache, fetchDetail]);

  const refetchActivePatientDetail = useCallback(() => {
    if (activePatientId) fetchDetail(activePatientId);
  }, [activePatientId, fetchDetail]);

  // isLoading only ever goes true for the very first load — every later
  // refresh (the user's own action, the live socket, or the fallback poll/
  // focus revalidation above) fetches silently and swaps the data in once
  // it arrives, with no loading flash. A ref (not a length check on the
  // fetched arrays) — a caregiver with genuinely zero patients/invites must
  // still count as "loaded" after the first successful fetch.
  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (!hasLoadedOnce.current) setIsLoading(true);

    Promise.all([listMyPatientsApi(), listIncomingInvitesApi()])
      .then(([patientsResult, invitesResult]) => {
        if (cancelled) return;
        if (patientsResult.error || invitesResult.error) {
          setError(patientsResult.error ?? invitesResult.error);
          return;
        }
        const nextPatients = patientsResult.data ?? [];
        setPatients(nextPatients);
        setInvites(invitesResult.data ?? []);
        setError(null);
        setActivePatientId((current) => {
          if (current && nextPatients.some((p) => p.id === current)) return current;
          return nextPatients.find((p) => p.status === "active")?.id ?? null;
        });
        hasLoadedOnce.current = true;
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load your patients. Please try again later.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  return (
    <CaregiverPatientsContext.Provider
      value={{
        patients,
        invites,
        isLoading,
        error,
        refresh,
        activePatientId,
        setActivePatientId,
        activePatientDetail: activePatientId ? detailCache[activePatientId] ?? null : null,
        isDetailLoading,
        refetchActivePatientDetail,
      }}
    >
      {children}
    </CaregiverPatientsContext.Provider>
  );
}

export function useCaregiverPatients() {
  const ctx = useContext(CaregiverPatientsContext);
  if (!ctx) throw new Error("useCaregiverPatients must be used within CaregiverPatientsProvider");
  return ctx;
}
